import React from 'react';
import PropTypes from 'prop-types';
import { useIntl, FormattedMessage } from 'react-intl';
import { useSelector } from 'react-redux';
import config from '../../config';
import * as types from '../../lib/constants';
import btnClose from '../../images/common/close.svg';
import {
  Main,
  Header,
  Contents,
  CloseButton,
  CloseButtonImage,
  UpdateButton,
  CancelButton,
  DeleteButton,
} from '../../styles/common/Dialog';
import {
  Dialog,
  Submit,
  InfoContents,
  BaseInfo,
  AccountInfo,
  InfoTitle,
  AccountInfoBody,
  UserInfoBody,
  SelectBox,
  ErrorMessage,
} from './styles/StaffDetailDialogStyles';
import { BoldText } from '../Group/styles/GroupDetailDialogStyles';
import ConfirmationDialog from '../Parts/ConfirmationDialog';
import createSelectOptions from './functions/createSelectOptions';
import useStaffActions from './hooks/useStaffActions';
import { isCompanyAdmin, isServiceVendor } from '../../lib/tools';
import useStaffDetail from './hooks/useStaffDetail';

const StaffDetailDialog = ({ role, enabledUpdateRemoteSupportStatus }) => {
  const intl = useIntl();
  const { formatMessage } = intl;
  const confirmData = useSelector(state => state.operator.confirmData);
  const {
    handleRoleSelect,
    handleStatusSelect,
    handleRemoteSupportStatusSelect,
  } = useStaffActions();

  const {
    isEditMode,
    showCancelAlertDialog,
    showDeleteAlertDialog,
    toggleDeleteAlert,
    toggleCancelAlert,
    handleClose,
    handleEditButtonClick,
    handleCancelButtonClick,
    handleUpdateButtonClick,
    handleDeleteButtonClick,
  } = useStaffDetail(confirmData);

  const { updateRole } = config.customRole;
  const updateRoles = updateRole[role];

  const isCompanyAdminUser = isCompanyAdmin(role);
  const isServiceVenderUser = isServiceVendor(role);

  const updateOperatorsError = confirmData.updateOperatorsError || {};
  const updateErrorMessageId = updateOperatorsError.userLicense
    ? `operator.updateStaff.message.error.accountStatus.${updateOperatorsError.errorCode}`
    : `operator.updateStaff.message.error.remoteSupportStatus.${updateOperatorsError.errorCode}`;

  return (
    <>
      <Main
        id="closeConfirmStaffWindow"
        role="presentation"
        onClick={handleClose}
      >
        <Dialog role="presentation" onClick={e => e.stopPropagation()}>
          <Contents isCompanyAdminUser={isCompanyAdminUser}>
            <Header>
              {isEditMode ? (
                <FormattedMessage id="staff.editDialog.title" />
              ) : (
                <FormattedMessage id="staff.detailDialog.title" />
              )}
              <CloseButton
                id="closeButton"
                role="presentation"
                className="button"
                onClick={handleClose}
              >
                <CloseButtonImage src={btnClose} alt="close" />
              </CloseButton>
            </Header>
            <InfoContents>
              <AccountInfo>
                <InfoTitle>
                  <FormattedMessage id="operator.OperatorLists.title.accountName" />
                </InfoTitle>
                <AccountInfoBody>{confirmData.name}</AccountInfoBody>
              </AccountInfo>
              <BaseInfo>
                <InfoTitle>
                  <FormattedMessage id="operator.OperatorLists.title.mailAddress" />
                </InfoTitle>
                <UserInfoBody>{confirmData.userId}</UserInfoBody>
              </BaseInfo>
              {/* 権限項目 */}
              <BaseInfo>
                <InfoTitle>
                  <FormattedMessage id="operator.OperatorLists.title.role" />
                </InfoTitle>
                <UserInfoBody>
                  {isEditMode ? (
                    <SelectBox
                      id="selectUpdateRole"
                      value={confirmData.role}
                      onChange={e => handleRoleSelect(e.target.value)}
                    >
                      {createSelectOptions(
                        updateRoles,
                        formatMessage,
                        'operator.role.status',
                      )}
                    </SelectBox>
                  ) : (
                    <FormattedMessage
                      id={`operator.role.status.${confirmData.role}`}
                    />
                  )}
                </UserInfoBody>
              </BaseInfo>
              {/* ライセンス項目 */}
              <BaseInfo>
                <InfoTitle>
                  <FormattedMessage id="operator.OperatorLists.title.license" />
                </InfoTitle>
                <UserInfoBody>
                  {isEditMode ? (
                    <SelectBox
                      id="selectUpdateAccountStatus"
                      value={confirmData.accountStatus}
                      onChange={e => handleStatusSelect(e.target.value)}
                    >
                      {createSelectOptions(
                        config.searchAccountStatus.notDeleted,
                        formatMessage,
                        'operator.accountStatus',
                      )}
                    </SelectBox>
                  ) : (
                    <FormattedMessage
                      id={`operator.accountStatus.${confirmData.accountStatus}`}
                    />
                  )}
                  {updateOperatorsError.userLicense && (
                    <ErrorMessage>
                      <FormattedMessage id={updateErrorMessageId} />
                    </ErrorMessage>
                  )}
                </UserInfoBody>
              </BaseInfo>
              {/* 遠隔支援項目 */}
              {enabledUpdateRemoteSupportStatus && (
                <BaseInfo>
                  <InfoTitle>
                    <FormattedMessage id="operator.OperatorLists.title.remoteSupportLicense" />
                  </InfoTitle>
                  <UserInfoBody>
                    {isEditMode ? (
                      <SelectBox
                        id="selectUpdateRemoteSupportStatus"
                        value={confirmData.remoteSupportStatus}
                        onChange={e =>
                          handleRemoteSupportStatusSelect(e.target.value)
                        }
                      >
                        {createSelectOptions(
                          types.REMOTE_SUPPORT_STATUS_ALL,
                          formatMessage,
                          'operator.remoteSupportStatus',
                        )}
                      </SelectBox>
                    ) : (
                      <FormattedMessage
                        id={`operator.remoteSupportStatus.${confirmData.remoteSupportStatus}`}
                      />
                    )}
                    {updateOperatorsError.remoteSupportLicense && (
                      <ErrorMessage>
                        <FormattedMessage id={updateErrorMessageId} />
                      </ErrorMessage>
                    )}
                  </UserInfoBody>
                </BaseInfo>
              )}
            </InfoContents>
            {/* ボタングループ */}
            {(isCompanyAdminUser || isServiceVenderUser) && (
              <Submit>
                {!isEditMode && (
                  <>
                    <DeleteButton onClick={() => toggleDeleteAlert()}>
                      <FormattedMessage id="common.button.delete" />
                    </DeleteButton>
                    <UpdateButton onClick={handleEditButtonClick}>
                      <FormattedMessage id="common.button.edit" />
                    </UpdateButton>
                  </>
                )}
                {isEditMode && (
                  <>
                    <CancelButton onClick={() => toggleCancelAlert()}>
                      <FormattedMessage id="common.button.cancel" />
                    </CancelButton>
                    <UpdateButton
                      id="updateButtonClick"
                      role="presentation"
                      className="button"
                      onClick={() => handleUpdateButtonClick()}
                    >
                      <FormattedMessage id="operator.updateStaff.button.title" />
                    </UpdateButton>
                  </>
                )}
              </Submit>
            )}
          </Contents>
        </Dialog>
      </Main>
      {showCancelAlertDialog && (
        <ConfirmationDialog
          headerText={<FormattedMessage id="staff.editDialog.title" />}
          messageText={
            <>
              <FormattedMessage id="staff.dialog.cancelConfirm.message1" />
              <br />
              <br />
              <FormattedMessage id="staff.dialog.cancelConfirm.message2" />
            </>
          }
          onCancel={() => toggleCancelAlert()}
          onConfirm={() => handleCancelButtonClick()}
          cancelButtonText={<FormattedMessage id="common.button.back" />}
          confirmButtonText={<FormattedMessage id="common.button.editCancel" />}
        />
      )}
      {showDeleteAlertDialog && (
        <ConfirmationDialog
          headerText={
            <FormattedMessage id="staff.dialog.deleteConfirm.title" />
          }
          messageText={
            <>
              <BoldText>{confirmData.name}</BoldText>
              <FormattedMessage id="staff.dialog.deleteConfirm.message1" />
              <br />
              <br />
              <FormattedMessage id="staff.dialog.deleteConfirm.message2" />
            </>
          }
          onCancel={() => toggleDeleteAlert()}
          onConfirm={() => handleDeleteButtonClick()}
        />
      )}
    </>
  );
};

StaffDetailDialog.propTypes = {
  role: PropTypes.number.isRequired,
  enabledUpdateRemoteSupportStatus: PropTypes.bool.isRequired,
};

export default StaffDetailDialog;
